<template>
  <div class="painel-dashboard">
    <PainelHeader />

    <div class="painel-dashboard__content">
      <button type="button" class="painel-dashboard__back" @click="goBack">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path d="M10 3L5 8l5 5V3z"/>
        </svg>
        Voltar
      </button>

    <div class="dashboard-header">
      <div class="header-top">
        <h1>Salas Registradas</h1>
        <div class="header-actions">
          <span v-if="atualizando" class="atualizando-badge">
            <span class="spinner"></span>
            Atualizando...
          </span>
          <button 
            class="btn-recarregar"
            @click="recarregarManual"
            :disabled="loading || atualizando"
            title="Recarregar salas"
          >
            <svg 
              width="16" 
              height="16" 
              viewBox="0 0 16 16" 
              fill="currentColor"
              :class="{ 'icon-spinning': atualizando }"
            >
              <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
              <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
            </svg>
            Recarregar
          </button>
        </div>
      </div>
      <div class="filter-container">
        <input
          v-model="filtro"
          type="text"
          placeholder="Buscar por nome da sala ou identificador..."
          class="filter-input"
        />
        <span class="filter-info">
          {{ salasFiltradas.length }} de {{ salas.length }} sala(s)
        </span>
      </div>
    </div>

    <div v-if="loading" class="loading">
      <p>Carregando salas...</p>
    </div>

    <div v-else-if="loadFailed" class="error">
      <p>Não foi possível carregar as salas.</p>
      <button @click="loadSalas">Tentar novamente</button>
    </div>

    <div v-else class="salas-container">
      <div v-if="salas.length === 0" class="empty-state">
        <p>Nenhuma sala encontrada</p>
      </div>

      <div v-else class="table-wrapper">
        <table class="salas-table">
          <thead>
            <tr>
              <th>Sala</th>
              <th>Estado</th>
              <th>Primário</th>
              <th>Secundário</th>
              <th>Duração da espera</th>
              <th>Duração da chamada</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="sala in salasFiltradas" 
              :key="sala.id"
            >
              <td class="sala-nome">{{ sala.nome }}</td>
              
              <td>
                <span 
                  class="sala-status" 
                  :class="'status-ativo'"
                >
                  {{ formatSalaStatus(resolveSalaEstado(sala)) }}
                </span>
              </td>
              
              <td class="uid-cell">
                <div class="uid-info">
                  <div class="uid-status">
                    {{ sala.uidPrimario || 'N/A' }}
                    <span 
                      class="status-badge"
                      :class="sala.primarioNaSala === sala.uidPrimario ? 'presente' : 'ausente'"
                    >
                      {{ sala.primarioNaSala === sala.uidPrimario ? 'Presente' : 'Ausente' }}
                    </span>
                  </div>
                  <button 
                    class="btn-analisar"
                    @click="analisarUID(sala.uidPrimario, sala.nome, 'primário')"
                    :disabled="!sala.uidPrimario"
                  >
                    Analisar
                  </button>
                </div>
              </td>
              
              <td class="uid-cell">
                <div class="uid-info">
                  <div class="uid-status">
                    {{ sala.uidSecundario || 'N/A' }}
                    <span 
                      class="status-badge"
                      :class="sala.secundarioNaSala === sala.uidSecundario ? 'presente' : 'ausente'"
                    >
                      {{ sala.secundarioNaSala === sala.uidSecundario ? 'Presente' : 'Ausente' }}
                    </span>
                  </div>
                  <button 
                    class="btn-analisar"
                    @click="analisarUID(sala.uidSecundario, sala.nome, 'secundário')"
                    :disabled="!sala.uidSecundario"
                  >
                    Analisar
                  </button>
                </div>
              </td>

              <td>{{ formatDuration(sala.tempoEsperaMs) }}</td>
              <td>{{ formatDuration(sala.duracaoChamadaMs) }}</td>
              
              <td class="acoes-cell">
                <div class="acoes-buttons">
                  <button 
                    class="btn-acao btn-logs"
                    @click="abrirLogs(sala.id, sala.nome)"
                    title="Ver logs da sala"
                  >
                    Logs
                  </button>
                  <button 
                    class="btn-acao btn-auditoria"
                    @click="abrirAuditoria(sala.id, sala.nome)"
                    title="Ver auditoria da sala"
                  >
                    Auditoria
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal de Análise de UID -->
    <ModalAnaliseUID
      :aberto="modalAberto"
      :uid="modalDados.uid"
      :sala="modalDados.sala"
      :tipo="modalDados.tipo"
      @fechar="fecharModal"
    />

    <!-- Modal de Logs da Sala -->
    <ModalLogsSala
      :aberto="modalLogsAberto"
      :salaId="modalLogsDados.salaId"
      :nomeSala="modalLogsDados.nomeSala"
      @fechar="fecharModalLogs"
    />

    <!-- Modal de Auditoria da Sala -->
    <ModalAuditoriaSala
      :aberto="modalAuditoriaAberto"
      :salaId="modalAuditoriaDados.salaId"
      :nomeSala="modalAuditoriaDados.nomeSala"
      @fechar="fecharModalAuditoria"
    />
    </div>
  </div>
</template>

<script>
import salasService from '../service/salas.service';
import { getClienteId, isAdminCliente, logout } from '../service/auth.service';
import ModalAnaliseUID from './components/ModalAnaliseUID.vue';
import ModalLogsSala from '../components/ModalLogsSala.vue';
import ModalAuditoriaSala from './components/ModalAuditoriaSala.vue';
import PainelHeader from '../components/PainelHeader.vue';
import { showErrorToast } from '../service/toast.service';
import { formatDuration, formatSalaStatus, resolveSalaEstado } from '../utils/salas.utils';

export default {
  name: 'PainelDashboard',

  components: {
    ModalAnaliseUID,
    ModalLogsSala,
    ModalAuditoriaSala,
    PainelHeader
  },

  data() {
    return {
      salas: [],
      loading: false,
      loadFailed: false,
      filtro: '',
      atualizando: false,
      updateInterval: null,
      modalAberto: false,
      modalDados: {
        uid: '',
        sala: '',
        tipo: ''
      },
      modalLogsAberto: false,
      modalLogsDados: {
        salaId: null,
        nomeSala: ''
      },
      modalAuditoriaAberto: false,
      modalAuditoriaDados: {
        salaId: null,
        nomeSala: ''
      }
    };
  },

  computed: {
    salasFiltradas() {
      if (!this.filtro.trim()) {
        return this.salas;
      }
      
      const termo = this.filtro.toLowerCase().trim();
      
      return this.salas.filter(sala => {
        // Busca pelo nome da sala
        if (sala.nome && sala.nome.toLowerCase().includes(termo)) {
          return true;
        }
        
        // Busca pelo UID primário
        if (sala.uidPrimario && sala.uidPrimario.toLowerCase().includes(termo)) {
          return true;
        }
        
        // Busca pelo UID secundário
        if (sala.uidSecundario && sala.uidSecundario.toLowerCase().includes(termo)) {
          return true;
        }
        
        return false;
      });
    }
  },

  mounted() {
    this.loadSalas();
    this.startAutoUpdate();
  },

  beforeDestroy() {
    this.stopAutoUpdate();
  },

  methods: {
    formatDuration,
    formatSalaStatus,
    resolveSalaEstado,

    goBack() {
      this.$router.push('/painel/dashboard');
    },

    async loadSalas(isAutoUpdate = false) {
      // Se for atualização automática, usa flag diferente
      if (isAutoUpdate) {
        this.atualizando = true;
      } else {
        this.loading = true;
      }
      this.loadFailed = false;
      
      const clienteId = getClienteId();
      if (!clienteId) {
        logout();
        this.$router.push('/painel');
        return;
      }

      try {
        this.salas = isAdminCliente()
          ? await salasService.findAll()
          : await salasService.findByCliente(clienteId);
      } catch (err) {
        if (!isAutoUpdate) {
          showErrorToast('Não foi possível carregar as salas. Tente novamente em instantes.');
          this.loadFailed = true;
        }
      } finally {
        this.loading = false;
        this.atualizando = false;
      }
    },

    startAutoUpdate() {
      // Atualiza a cada 5 minutos (5 * 60000ms)
      this.updateInterval = setInterval(() => {
        this.loadSalas(true);
      }, 5 * 60000);
    },
    stopAutoUpdate() {
      if (this.updateInterval) {
        clearInterval(this.updateInterval);
        this.updateInterval = null;
      }
    },

    recarregarManual() {
      this.loadSalas(true);
    },

    analisarUID(uid, sala, tipo) {
      this.modalAberto = true;
      this.modalDados = {
        uid,
        sala,
        tipo
      };
    },

    fecharModal() {
      this.modalAberto = false;
      this.modalDados = {
        uid: '',
        sala: '',
        tipo: ''
      };
    },

    abrirLogs(salaId, nomeSala) {
      this.modalLogsAberto = true;
      this.modalLogsDados = {
        salaId,
        nomeSala
      };
    },

    fecharModalLogs() {
      this.modalLogsAberto = false;
      this.modalLogsDados = {
        salaId: null,
        nomeSala: ''
      };
    },

    abrirAuditoria(salaId, nomeSala) {
      this.modalAuditoriaAberto = true;
      this.modalAuditoriaDados = {
        salaId,
        nomeSala
      };
    },

    fecharModalAuditoria() {
      this.modalAuditoriaAberto = false;
      this.modalAuditoriaDados = {
        salaId: null,
        nomeSala: ''
      };
    }
  }
};
</script>

<style scoped src="./PainelDashboard.css"></style>
